import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  GitMerge,
  Layers,
  Loader2,
  Radar,
  Sparkles,
  TriangleAlert,
  X,
} from 'lucide-react';

import { MathTextBlock } from '../components/MathTextBlock';
import { SectionCard } from '../components/ttq/SectionCard';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { accountUserProfileService } from '../services/accountUserProfileService';
import {
  getLearningSequenceSettings,
  updateLearningSequenceSettings,
} from '../services/reviewService';
import {
  formatMembershipPressureWorkerScanLabel,
  ttqMembershipPressureService,
  type TtqMembershipPressureDebugScanResult,
  type TtqMembershipPressureParticlePreview,
  type TtqMembershipPressureRecommendation,
  type TtqMembershipPressureWorkerStatus,
} from '../services/ttqMembershipPressureService';
import { isTtqSubjectEnabled, resolveTtqDockSubject } from '../services/ttqService';
import { SUBJECT_CONFIG } from '../utils/subjectConfig';
import {
  formatSpineLabel,
  recommendationListMeta,
  recommendationListTitle,
} from '../utils/ttqMembershipPressureDisplay';

const AUTO_SCAN_LIMIT = 12;

function formatScore(value: number | undefined) {
  return Number.isFinite(value) ? Number(value).toFixed(2) : '—';
}

function shortPreview(text: string | null | undefined, max = 36): string {
  const value = String(text || '').replace(/\s+/g, ' ').trim();
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;
}

function ParticleChip(props: {
  label: string;
  tone?: 'source' | 'peer' | 'candidate';
  onOpen: () => void;
}) {
  const toneClass = props.tone === 'source'
    ? 'border-primary/40 bg-primary/10 text-primary'
    : props.tone === 'candidate'
      ? 'border-border bg-surface text-muted'
      : 'border-border bg-canvas text-ink';

  return (
    <button
      type="button"
      onClick={props.onOpen}
      title={props.label}
      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition hover:border-primary/50 hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${toneClass}`}
      aria-label={`查看主问题：${props.label}`}
    >
      <BookOpen size={16} aria-hidden />
    </button>
  );
}

function ParticleDetailPopup(props: {
  preview: TtqMembershipPressureParticlePreview | null;
  roleLabel?: string;
  onClose: () => void;
}) {
  if (!props.preview) return null;
  const main = String(props.preview.mainQuestion || props.preview.title || '').trim();

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-4 sm:items-center">
      <button type="button" className="absolute inset-0" aria-label="关闭" onClick={props.onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="membership-pressure-particle-popup-title"
        className="relative z-10 flex max-h-[min(80vh,520px)] w-full max-w-lg flex-col rounded-2xl border border-border bg-surface shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-border/70 px-4 py-3">
          <div className="min-w-0">
            <p id="membership-pressure-particle-popup-title" className="text-sm font-semibold text-ink">
              {props.roleLabel || '知识颗粒'} · 主问题
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-subtle">
              {props.preview.particleId.slice(0, 8)}…
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted hover:bg-black/[0.04]"
            aria-label="关闭"
          >
            <X size={14} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {main ? (
            <MathTextBlock text={main} variant="document" className="text-sm leading-relaxed text-ink" />
          ) : (
            <p className="text-sm text-muted">暂无主问题内容</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function TtqMembershipPressurePage() {
  const { user } = useAuth();
  const { activeSubject: subject } = useActiveSubject();
  const ttqSubject = isTtqSubjectEnabled(subject) ? resolveTtqDockSubject(subject) : null;
  const subjectLabel = SUBJECT_CONFIG[subject]?.label ?? subject;

  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [enablingValve, setEnablingValve] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [ipttqValveOn, setIpttqValveOn] = useState(false);
  const [workerStatus, setWorkerStatus] = useState<TtqMembershipPressureWorkerStatus | null>(null);
  const [recs, setRecs] = useState<TtqMembershipPressureRecommendation[]>([]);
  const didAutoScanRef = useRef(false);

  const [debugBusy, setDebugBusy] = useState(false);
  const [debugCommit, setDebugCommit] = useState(false);
  const [debugResult, setDebugResult] = useState<TtqMembershipPressureDebugScanResult | null>(null);
  const [debugExpandedId, setDebugExpandedId] = useState<string | null>(null);
  const [popupParticleId, setPopupParticleId] = useState<string | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);

  const particlePreviews = useMemo(
    () => debugResult?.particlePreviews || {},
    [debugResult],
  );

  const popupPreview = popupParticleId
    ? particlePreviews[popupParticleId] || {
      particleId: popupParticleId,
      title: null,
      mainQuestion: null,
    }
    : null;

  const resolvePreview = useCallback((particleId: string): TtqMembershipPressureParticlePreview => {
    return particlePreviews[particleId] || {
      particleId,
      title: null,
      mainQuestion: null,
    };
  }, [particlePreviews]);

  const fetchRecommendations = useCallback(async () => {
    if (!ttqSubject) return [] as TtqMembershipPressureRecommendation[];
    const rows = await ttqMembershipPressureService.listRecommendations('recommended');
    return rows.filter((row) => !row.subjectCode || row.subjectCode === ttqSubject);
  }, [ttqSubject]);

  const runCommittedScan = useCallback(async () => {
    if (!ttqSubject) return null;
    return ttqMembershipPressureService.debugScan({
      commit: true,
      limit: AUTO_SCAN_LIMIT,
      subjectCode: ttqSubject,
    });
  }, [ttqSubject]);

  const load = useCallback(async (options?: { autoScanIfEmpty?: boolean }) => {
    const userId = String(user?.id || '').trim();
    const autoScanIfEmpty = options?.autoScanIfEmpty !== false;
    setLoading(true);
    setError(null);
    try {
      let valve = false;
      if (userId) {
        valve = Boolean(getLearningSequenceSettings(userId).includeIpttqInReviewSequence);
        try {
          const remote = await accountUserProfileService.getLearningSequence(userId);
          if (typeof remote.includeIpttqInReviewSequence === 'boolean') {
            valve = remote.includeIpttqInReviewSequence;
            updateLearningSequenceSettings(userId, { includeIpttqInReviewSequence: valve });
          }
        } catch {
          // local fallback
        }
      }
      setIpttqValveOn(valve);

      const status = await ttqMembershipPressureService.getWorkerStatus().catch(() => null);
      setWorkerStatus(status);

      if (!valve || !ttqSubject) {
        setRecs([]);
        return;
      }

      let filtered = await fetchRecommendations();

      if (
        filtered.length === 0
        && autoScanIfEmpty
        && !didAutoScanRef.current
      ) {
        didAutoScanRef.current = true;
        setScanning(true);
        try {
          const scan = await runCommittedScan();
          if (scan?.skippedReason) {
            setScanNote(`扫描跳过：${scan.skippedReason}`);
          } else if (scan) {
            setScanNote(
              `已扫描孤儿 ${scan.orphanTotal} · 本次 ${scan.scannedCount} · 写入 ${scan.recommendationWritten ?? 0}`,
            );
          }
          filtered = await fetchRecommendations();
        } catch (scanError) {
          setError(scanError instanceof Error ? scanError.message : '扫描入围推荐失败');
        } finally {
          setScanning(false);
        }
      }

      setRecs(filtered);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : '加载入围推荐失败');
    } finally {
      setLoading(false);
    }
  }, [fetchRecommendations, runCommittedScan, ttqSubject, user?.id]);

  useEffect(() => {
    didAutoScanRef.current = false;
    void load({ autoScanIfEmpty: true });
  }, [load]);

  const handleManualScan = async () => {
    if (!ttqSubject || !ipttqValveOn || scanning) return;
    setScanning(true);
    setError(null);
    try {
      const scan = await runCommittedScan();
      if (scan?.skippedReason) {
        setScanNote(`扫描跳过：${scan.skippedReason}`);
      } else if (scan) {
        setScanNote(
          `已扫描孤儿 ${scan.orphanTotal} · 本次 ${scan.scannedCount} · 写入 ${scan.recommendationWritten ?? 0}`,
        );
      }
      const filtered = await fetchRecommendations();
      setRecs(filtered);
      const status = await ttqMembershipPressureService.getWorkerStatus().catch(() => null);
      setWorkerStatus(status);
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : '扫描入围推荐失败');
    } finally {
      setScanning(false);
    }
  };

  const handleEnableValve = async () => {
    const userId = String(user?.id || '').trim();
    if (!userId || enablingValve) return;
    setEnablingValve(true);
    setError(null);
    try {
      await accountUserProfileService.patchLearningSequence(userId, {
        includeIpttqInReviewSequence: true,
      });
      updateLearningSequenceSettings(userId, { includeIpttqInReviewSequence: true });
      setIpttqValveOn(true);
      didAutoScanRef.current = false;
      await load({ autoScanIfEmpty: true });
    } catch (enableError) {
      setError(enableError instanceof Error ? enableError.message : '开启品题加入复习列失败');
    } finally {
      setEnablingValve(false);
    }
  };

  const handleDebugScan = async () => {
    setDebugBusy(true);
    setError(null);
    try {
      const result = await ttqMembershipPressureService.debugScan({
        commit: debugCommit,
        limit: 8,
        subjectCode: ttqSubject || 'math',
      });
      setDebugResult(result);
      setDebugExpandedId(result.items[0]?.particleId ?? null);
      setShowRawJson(false);
      if (debugCommit) {
        await load({ autoScanIfEmpty: false });
      }
    } catch (scanError) {
      setError(scanError instanceof Error ? scanError.message : '手动扫描失败');
    } finally {
      setDebugBusy(false);
    }
  };

  if (!ttqSubject) {
    return (
      <div className="mx-auto max-w-3xl space-y-4">
        <h1 className="text-2xl font-semibold text-ink">入围推荐</h1>
        <SectionCard tone="muted">
          <p className="text-sm text-muted">当前学科暂未开放品题。</p>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to="/ttq"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-ink hover:bg-black/[0.04]"
            aria-label="返回品题列表"
          >
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-ink">{subjectLabel}入围推荐</h1>
            <p className="mt-1 text-sm text-muted">
              点击条目查看融合详情；确认后再决定是否构造品题。
            </p>
            <p className="mt-1 text-xs tabular-nums text-muted">
              {formatMembershipPressureWorkerScanLabel(workerStatus)}
            </p>
            {scanNote ? (
              <p className="mt-1 text-xs text-muted">{scanNote}</p>
            ) : null}
          </div>
        </div>
        {ipttqValveOn ? (
          <button
            type="button"
            disabled={scanning || loading}
            onClick={() => { void handleManualScan(); }}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-primary hover:bg-primary/10 disabled:opacity-50"
            aria-label="扫描推荐"
          >
            {scanning ? <Loader2 size={14} className="animate-spin" /> : <Radar size={14} />}
            {scanning ? '扫描中' : '扫描'}
          </button>
        ) : null}
      </div>

      {!loading && !ipttqValveOn ? (
        <SectionCard tone="muted">
          <p className="flex items-start gap-2 text-sm font-medium text-amber-700">
            <TriangleAlert size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden />
            <span>请先开启「品题加入复习列」，才会扫描并产生入围推荐。</span>
          </p>
          <button
            type="button"
            disabled={enablingValve}
            onClick={() => { void handleEnableValve(); }}
            className="mt-3 inline-flex h-9 items-center rounded-lg bg-primary px-3 text-sm font-medium text-white disabled:opacity-60"
          >
            {enablingValve ? '开启中…' : '一键开启并扫描'}
          </button>
        </SectionCard>
      ) : null}

      {error ? <p className="text-sm text-danger">{error}</p> : null}

      {loading || scanning ? (
        <div className="flex flex-col items-center gap-2 py-12">
          <Loader2 className="animate-spin text-primary" size={28} />
          <p className="text-sm text-muted">
            {scanning ? '正在扫描未入品题的知识颗粒…' : '加载中…'}
          </p>
        </div>
      ) : null}

      {!loading && !scanning && ipttqValveOn && recs.length === 0 ? (
        <SectionCard tone="muted">
          <p className="text-sm text-muted">
            暂无待确认推荐。可能暂无孤儿颗粒，或扫描未找到合适同伴。可再点右上角「扫描」重试。
          </p>
        </SectionCard>
      ) : null}

      <div className="space-y-3">
        {recs.map((rec) => {
          const title = recommendationListTitle(rec);
          return (
            <Link
              key={rec.id}
              to={`/ttq/recommendations/${rec.id}`}
              className="block"
            >
              <SectionCard tone="accent" className="transition hover:border-primary/40">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <MathTextBlock
                      text={title}
                      className="line-clamp-3 text-base font-semibold leading-snug text-ink"
                    />
                    <p className="mt-1 text-[11px] text-muted">
                      学科主干：{formatSpineLabel(rec.sourceSpineLabel)}
                    </p>
                    <p className="mt-1 text-xs text-muted">{recommendationListMeta(rec)}</p>
                  </div>
                  <ChevronRight className="mt-1 shrink-0 text-muted" size={20} aria-hidden />
                </div>
              </SectionCard>
            </Link>
          );
        })}
      </div>

      <details
        className="rounded-2xl border border-border/70 bg-canvas/40 p-4"
        open={debugOpen}
        onToggle={(event) => setDebugOpen(event.currentTarget.open)}
      >
        <summary className="cursor-pointer text-xs font-medium text-muted">
          扫描调试（预览，默认不写入）
        </summary>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={debugBusy || !ipttqValveOn}
            onClick={() => { void handleDebugScan(); }}
            className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-surface px-3 text-xs font-medium text-ink hover:bg-black/[0.04] disabled:opacity-50"
          >
            {debugBusy ? <Loader2 size={12} className="animate-spin" /> : <Radar size={12} />}
            手动扫描调试
          </button>
          <label className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted">
            <input
              type="checkbox"
              checked={debugCommit}
              onChange={(event) => setDebugCommit(event.target.checked)}
              className="rounded border-border"
            />
            写入推荐（默认仅预览）
          </label>
        </div>

        {debugResult ? (
          <div className="mt-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink">扫描调试明细</p>
                <p className="mt-1 text-xs text-muted">
                  {debugResult.commit ? '已写入推荐' : '预览模式（未写入）'}
                  {' · '}孤儿共 {debugResult.orphanTotal}
                  {' · '}本次扫描 {debugResult.scannedCount}
                  {' · '}提案 {debugResult.proposalCount ?? 0}
                  {debugResult.commit
                    ? ` · 写入 ${debugResult.recommendationWritten ?? 0}`
                    : ''}
                </p>
                <p className="mt-1 text-[11px] text-subtle">点击书本图标可查看主问题</p>
              </div>
              <button
                type="button"
                className="text-xs text-muted underline"
                onClick={() => setDebugResult(null)}
              >
                收起
              </button>
            </div>

            {debugResult.skippedReason ? (
              <p className="mt-2 text-sm text-amber-700">跳过：{debugResult.skippedReason}</p>
            ) : null}

            <div className="mt-3 space-y-2">
              {debugResult.items.map((item) => {
                const open = debugExpandedId === item.particleId;
                const sourcePreview = resolvePreview(item.particleId);
                const headline = shortPreview(
                  item.mainQuestion || sourcePreview.mainQuestion || item.title,
                  48,
                ) || item.particleId.slice(0, 8);
                return (
                  <div key={item.particleId} className="rounded-lg border border-border/70 bg-canvas/40">
                    <div className="flex items-start gap-2 px-3 py-2">
                      <ParticleChip
                        label={headline}
                        tone="source"
                        onOpen={() => setPopupParticleId(item.particleId)}
                      />
                      <button
                        type="button"
                        className="min-w-0 flex-1 text-left"
                        onClick={() => setDebugExpandedId(open ? null : item.particleId)}
                      >
                        <p className="line-clamp-2 text-sm font-medium text-ink">{headline}</p>
                        <p className="mt-0.5 text-[11px] text-muted">
                          候选 {item.scan.scoredCandidateCount}
                          {' · '}提案 {item.scan.proposals?.length ?? 0}
                          {item.scan.skippedReason ? ` · ${item.scan.skippedReason}` : ''}
                        </p>
                      </button>
                      <span className="shrink-0 pt-1 text-xs text-muted">{open ? '收起' : '展开'}</span>
                    </div>
                    {open ? (
                      <div className="space-y-3 border-t border-border/60 px-3 py-3 text-xs">
                        {(item.scan.topCandidates || []).length > 0 ? (
                          <div>
                            <p className="mb-2 flex items-center gap-1.5 font-semibold text-ink">
                              <Layers size={13} aria-hidden />
                              Top 候选
                            </p>
                            <ul className="space-y-2">
                              {(item.scan.topCandidates || []).map((candidate, index) => {
                                const preview = resolvePreview(candidate.particleId);
                                const label = shortPreview(
                                  item.scan.topCandidateTitles?.[index]
                                    || preview.mainQuestion
                                    || preview.title,
                                  40,
                                ) || candidate.particleId.slice(0, 8);
                                return (
                                  <li key={candidate.particleId} className="flex items-start gap-2">
                                    <ParticleChip
                                      label={label}
                                      tone="candidate"
                                      onOpen={() => setPopupParticleId(candidate.particleId)}
                                    />
                                    <div className="min-w-0 pt-1 text-muted">
                                      <p className="line-clamp-2 text-ink">{label}</p>
                                      <p className="mt-0.5 tabular-nums">
                                        Ring{candidate.ring}
                                        {' · '}总分 {formatScore(candidate.scores.total)}
                                        {'（主干 '}
                                        {formatScore(candidate.scores.trunkProximity)}
                                        {' / 同质 '}
                                        {formatScore(candidate.scores.typeHomogeneity)}
                                        {' / 能力 '}
                                        {formatScore(candidate.scores.implicitAbility)}
                                        {'）'}
                                      </p>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}

                        {(item.scan.proposals || []).length > 0 ? (
                          <div>
                            <p className="mb-2 flex items-center gap-1.5 font-semibold text-ink">
                              <GitMerge size={13} aria-hidden />
                              融合提案
                            </p>
                            <ul className="space-y-2">
                              {(item.scan.proposals || []).map((proposal, index) => {
                                const peerIds = proposal.suggestedPeerIds;
                                const isJoin = proposal.action === 'join_existing_ttqa';
                                return (
                                  <li
                                    key={`${proposal.action}-${proposal.targetTtqaId || index}`}
                                    className="rounded-md border border-border/60 bg-surface p-2.5"
                                  >
                                    <p className="flex items-center gap-1.5 font-medium text-ink">
                                      {isJoin ? <GitMerge size={13} aria-hidden /> : <Sparkles size={13} aria-hidden />}
                                      {isJoin
                                        ? `加入已有品题 ${proposal.targetTtqaId?.slice(0, 8) || '—'}`
                                        : `新开品题 · 同伴 ${peerIds.length}`}
                                      <span className="font-normal text-muted">
                                        · Ring{proposal.ringEvidence?.ring ?? '—'}
                                        · 分 {formatScore(proposal.scores.total)}
                                      </span>
                                    </p>
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                      <span className="mr-1 text-[11px] text-subtle">源</span>
                                      <ParticleChip
                                        label={headline}
                                        tone="source"
                                        onOpen={() => setPopupParticleId(item.particleId)}
                                      />
                                      {peerIds.length > 0 ? (
                                        <>
                                          <span className="mx-1 text-[11px] text-subtle">+</span>
                                          {peerIds.map((peerId, peerIndex) => {
                                            const peerPreview = resolvePreview(peerId);
                                            const peerLabel = shortPreview(
                                              item.scan.proposalPeerTitles?.[index]?.[peerIndex]
                                                || peerPreview.mainQuestion
                                                || peerPreview.title,
                                              28,
                                            ) || peerId.slice(0, 8);
                                            return (
                                              <ParticleChip
                                                key={peerId}
                                                label={peerLabel}
                                                tone="peer"
                                                onOpen={() => setPopupParticleId(peerId)}
                                              />
                                            );
                                          })}
                                        </>
                                      ) : null}
                                    </div>
                                    {proposal.ringEvidence?.sharedAbilityKeys?.length ? (
                                      <p className="mt-2 text-muted">
                                        共享能力：{proposal.ringEvidence.sharedAbilityKeys.slice(0, 6).join('、')}
                                      </p>
                                    ) : null}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : (
                          <p className="text-muted">无提案（可能被反例或去重挡住）</p>
                        )}

                        <button
                          type="button"
                          className="text-[11px] text-muted underline"
                          onClick={() => setShowRawJson((value) => !value)}
                        >
                          {showRawJson ? '隐藏原始 JSON' : '显示原始 JSON'}
                        </button>
                        {showRawJson ? (
                          <pre className="max-h-48 overflow-auto rounded-md bg-ink/[0.04] p-2 font-mono text-[10px] leading-4 text-muted">
                            {JSON.stringify(item.scan, null, 2)}
                          </pre>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {debugResult.items.length === 0 ? (
                <p className="text-sm text-muted">本次没有可扫描的孤儿颗粒。</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </details>

      <ParticleDetailPopup
        preview={popupPreview}
        roleLabel="知识颗粒"
        onClose={() => setPopupParticleId(null)}
      />
    </div>
  );
}
